import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Chip, MenuItem, TextField, Tab, Tabs } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import PageShell from "../PageShell";
import PageContainer from "../components/PageContainer";
import PageHeader from "../components/PageHeader";
import AuditTrailPanel from "./AuditTrailPanel";
import LogsPanel from "./LogsPanel";
import { TIME_RANGES, sinceFor } from "./activityFilters";

/** Activity — one place for what happened: the Audit trail and the Logs. Tab, time range and request
 *  id live in the URL (/activity?tab=logs&request=req_…); clicking a request id
 *  in either tab shows that request's logs. Admin only (menu + API roles). */
export default function Activity() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "logs" ? "logs" : "audit";
  const requestId = params.get("request") || "";
  const range = params.get("range") || "all";
  // Recomputed only when the range changes — a fresh `since` every render
  // would refetch forever.
  const since = useMemo(() => sinceFor(range), [range]);

  const update = (changes) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      return next;
    }, { replace: true });
  };
  const onRequestId = (id) => update({ request: id, tab: "logs" });
  const filters = { requestId, since, onRequestId };

  return (
    <PageShell>
      <PageContainer>
        <PageHeader icon={<HistoryIcon />} title="Activity" subtitle="Audit trail and logs" />

        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tab} onChange={(_, v) => update({ tab: v })}>
            <Tab value="audit" label="Audit trail" />
            <Tab value="logs" label="Logs" />
          </Tabs>
          <Box sx={{ flex: 1 }} />
          {requestId && (
            <Chip
              label={`request ${requestId}`}
              onDelete={() => update({ request: null })}
              sx={{ fontFamily: "monospace" }}
            />
          )}
          <TextField
            select
            size="small"
            label="Time range"
            value={range}
            onChange={(e) => update({ range: e.target.value === "all" ? null : e.target.value })}
            sx={{ minWidth: 160, my: 1 }}
          >
            {TIME_RANGES.map((r) => <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>)}
          </TextField>
        </Box>

        <Box sx={{ pt: 2 }}>
          {tab === "logs"
            ? <LogsPanel {...filters} initialQuery={params.get("q") || ""} />
            : <AuditTrailPanel {...filters} />}
        </Box>
      </PageContainer>
    </PageShell>
  );
}
